import React, { useEffect, useState } from 'react';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../TwAutocomplete/TwAutocomplete';
import { SxProps } from '@mui/material';
import { useSearchOrderFieldQuery, useSearchOrdersQuery } from '../../redux/api/orders/ordersApi';
import {
  CLIENT_TYPE,
  SearchOrderFieldResponse,
  SearchOrdersResponse
} from '../../redux/api/orders/orders.model';

type OrderSelectorProps = {
  label?: string;
  selectedOrder: TwAutocompleteOption | null | undefined;
  hasError: boolean;
  onChange: (value: string) => void;
  setSelectedOrder: (driver: TwAutocompleteOption | null) => void;
  sx?: SxProps;
  readOnly?: boolean;
  disabled?: boolean;
};

const TwOrderSelector: React.FC<OrderSelectorProps> = ({
  label,
  selectedOrder,
  hasError,
  setSelectedOrder,
  onChange,
  sx = {},
  readOnly = false,
  disabled = false
}): JSX.Element => {
  const [orderOptions, setOrderOptions] = React.useState<TwAutocompleteOption[]>([]);
  const [searchOrder, setSearchOrder] = useState<string>('');
  const [blockFetchOrders, setBlockFetchOrders] = useState<boolean>(false);

  // Fetch order list
  const { data: orderList = { content: [], total: 0 } } = useSearchOrdersQuery(
    {
      params: {
        limit: 10,
        offset: 0
      }
    },
    {
      skip: blockFetchOrders || readOnly || searchOrder !== ''
    }
  );

  // Fetch order list by fields
  const { data: orderListByFields = { content: [], total: 0 } } = useSearchOrderFieldQuery(
    {
      field: ['readable_id', 'customer_order_number', 'primary_client'],
      search: searchOrder
    },
    {
      skip: blockFetchOrders || readOnly || searchOrder === ''
    }
  );

  const getOrderLabel = (order: SearchOrdersResponse): string => {
    // Structure: Customer name | Cust # | TB #
    return `${order.primary_client_name} | CUST # ${order.customer_order_number || '-'} | TB # ${
      order.readable_id || '-'
    } `;
  };

  const getOrderLabelByFields = (order: SearchOrderFieldResponse): string => {
    const customerName =
      order.primary_client === CLIENT_TYPE.BROKER
        ? order.broker?.name
        : order.primary_client === CLIENT_TYPE.SHIPPER
        ? order.shipper?.name
        : '';
    return `${customerName} | CUST # ${order.customer_order_number || '-'} | TB # ${
      order.readable_id || '-'
    } `;
  };

  // Transform order list
  const transformOrderListByFields = (
    orders: SearchOrderFieldResponse[]
  ): TwAutocompleteOption[] => {
    return orders.map((order) => ({
      id: order.id,
      value: order.id,
      label: getOrderLabelByFields(order)
    }));
  };

  const transformOrderList = (orders: SearchOrdersResponse[]): TwAutocompleteOption[] => {
    return orders.map((order) => ({
      id: order.id,
      value: order.id,
      label: getOrderLabel(order)
    }));
  };

  useEffect(() => {
    if (orderListByFields.content.length > 0) {
      setBlockFetchOrders(true);
      setOrderOptions(transformOrderListByFields(orderListByFields.content));
    }
  }, [JSON.stringify(orderListByFields.content)]);

  useEffect(() => {
    if (orderList.content.length > 0) {
      setBlockFetchOrders(true);
      setOrderOptions(transformOrderList(orderList.content));
    }
  }, [JSON.stringify(orderList.content)]);

  return (
    <TwAutocomplete
      label={label}
      name="order"
      filterOptions={(x: any): any => x}
      renderOption={(props, option): React.ReactNode => {
        return <RenderOption key={option.id} props={props} option={option} />;
      }}
      optionList={orderOptions}
      value={selectedOrder || null}
      onChange={(_, newValue: TwAutocompleteOption | null): void => {
        const selection = newValue ? ({ ...newValue } as TwAutocompleteOption) : null;
        setSelectedOrder(selection);
        onChange(newValue?.id || '');
      }}
      onInputChange={(_, newInputValue): void => {
        setSearchOrder(newInputValue || '');
        setBlockFetchOrders(false);
      }}
      error={hasError}
      sx={sx}
      readonly={readOnly}
      showEndAdornment={!readOnly}
      disabled={readOnly ? false : disabled}
    />
  );
};

export default TwOrderSelector;
